import { getDb } from "./db";

const PERSISTED_KEY = "persisted";

export interface StorageEstimate {
  usage?: number;
  quota?: number;
  usageDetails?: Record<string, number>;
}

export async function requestPersistence(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.storage?.persist) return false;
  try {
    const granted = await navigator.storage.persist();
    await setPersistenceStatus(granted);
    return granted;
  } catch {
    return false;
  }
}

export async function getPersistenceStatus(): Promise<boolean | undefined> {
  try {
    const row = await getDb().syncMeta.get(PERSISTED_KEY);
    return row ? (row.cursor === "true") : undefined;
  } catch {
    return undefined;
  }
}

export async function setPersistenceStatus(granted: boolean): Promise<void> {
  try {
    await getDb().syncMeta.put({
      collection: PERSISTED_KEY,
      cursor: granted ? "true" : "false",
    });
  } catch {
    /* ignore */
  }
}

export async function getStorageEstimate(): Promise<StorageEstimate | null> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) return null;
  try {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage ?? undefined,
      quota: estimate.quota ?? undefined,
      usageDetails: (estimate as { usageDetails?: Record<string, number> }).usageDetails,
    };
  } catch {
    return null;
  }
}

export function isLowStorage(estimate: StorageEstimate | null, freeBytesThreshold = 50 * 1024 * 1024): boolean {
  if (!estimate) return false;
  if (estimate.usage != null && estimate.quota != null) {
    const free = estimate.quota - estimate.usage;
    const percentUsed = estimate.usage / estimate.quota;
    return free < freeBytesThreshold || percentUsed > 0.85;
  }
  return false;
}
